import React, { useState } from "react";
import {
  Empty,
  Table,
  Tag,
  Image,
  Dropdown,
  Button,
  Modal,
  Select,
  Input,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  MinusCircleOutlined,
  HistoryOutlined,
  PoweroffOutlined,
  DeleteOutlined,
  MoreOutlined,
  SearchOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { PAGE_SIZE, IMAGE_URL, SHORTCUTS } from "../../Constant";
import { PageHeader, tablePagination } from "../../components";
import { Product, ProductAction } from "../../Types";
import {
  BulkImportReport,
  ProductForm,
  StockHistoryDrawer,
  StockTable,
  ProductBarcodePrintModal,
} from "./common";
import { useProductsPage } from "./useProducts";
import { useSectionAccess } from "../../Services";

const { Option } = Select;

export const ProductsPage = () => {
  const {
    drawerOpen,
    editingProduct,
    excelSheetUploading,
    fileInputRef,
    form,
    handleFileImport,
    handleSubmitProduct,
    historyDrawerOpen,
    historyProduct,
    importErrors,
    importReportOpen,
    importSuccessMessage,
    isLoading,
    page,
    products,
    setImportReportOpen,
    setPage,
    submitLoading,
    total,
    triggerImportClick,
    closeDrawer,
    closeHistoryDrawer,
    openDrawer,
    handleTogglePos,
    handleToggleNegativeQty,
    handleToggleStatus,
    handleDelete,
    prefetchProductLookups,
    refetch,
    categories,
    brands,
    categoriesRequest,
    brandsRequest,
    handleCategoryFilter,
    handleBrandFilter,
    searchInputRef,
    searchInput,
    handleSearchChange,
    setSearchInput,
    setHistoryProduct,
    setHistoryDrawerOpen,
  } = useProductsPage();

  const [barcodePrintModalOpen, setBarcodePrintModalOpen] = useState(false);
  const [barcodePrintProduct, setBarcodePrintProduct] =
    useState<Product | null>(null);

  const { isAllowed } = useSectionAccess("products");

  const columns = [
    {
      title: "Image",
      key: "image",
      width: 100,
      render: (_: any, rec: Product) => {
        let imgs: string[] = [];
        try {
          imgs = JSON.parse(rec.image_paths || "[]");
        } catch {
          imgs = [];
        }
        const mainImage = imgs.length > 0 ? `${IMAGE_URL}/${imgs[0]}` : null;
        return (
          <div className="w-12 h-12 rounded border border-gray-100 flex items-center justify-center overflow-hidden bg-gray-50">
            {mainImage ? (
              <Image
                src={mainImage}
                alt={rec.title}
                width={48}
                height={48}
                style={{ objectFit: "cover" }}
              />
            ) : (
              <span className="text-gray-300 text-xs">No Img</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Product Details",
      key: "details",
      render: (_: any, rec: Product) => (
        <div>
          <p className="font-semibold text-gray-800 text-sm m-0 leading-snug">
            {rec.title}
          </p>
          <div className="flex flex-col items-start mt-1">
            <span className="text-xs text-gray-400">SKU: {rec.sku_code}</span>
            <span className="text-xs text-gray-400">
              Barcode: {rec.barcode}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Cat/Brand/Store",
      key: "category_brand",
      render: (_: any, rec: Product) => (
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap gap-1">
            <Tag color="cyan" className="m-0 text-[10px] px-1.5 py-0.5">
              {rec.category_name || "Uncategorized"}
            </Tag>
            <Tag color="blue" className="m-0 text-[10px] px-1.5 py-0.5">
              {rec.brand_name || "No Brand"}
            </Tag>
          </div>
          <div className="flex items-center gap-1 text-[11px] text-gray-500 font-medium mt-0.5">
            {(rec as any).store_type === "vehicle" ? "🚚" : "🏢"}
            <span className="text-gray-700 font-semibold ml-0.5">
              {(rec as any).store_name || "Main Store"}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      render: (v: string) => <Tag color="orange">{v}</Tag>,
    },
    {
      title: "MRP",
      dataIndex: "mrp",
      key: "mrp",
      render: (v: number) => (
        <span className="font-semibold text-gray-800">Rs. {v.toFixed(2)}</span>
      ),
    },
    {
      title: "Qty",
      dataIndex: "total_quantity",
      key: "total_quantity",
      render: (v: number, rec: Product) => {
        const isLow = v <= rec.alert_quantity;
        return (
          <div>
            <span
              className={`font-semibold text-sm ${isLow ? "text-red-500 animate-pulse" : "text-gray-700"}`}
            >
              {v}
            </span>
            {isLow && (
              <p className="m-0 text-red-400" style={{ fontSize: "10px" }}>
                (Alert: {rec.alert_quantity})
              </p>
            )}
          </div>
        );
      },
    },
    {
      title: "In POS",
      key: "is_pos",
      width: 90,
      render: (_: any, rec: Product) => {
        const inPos = rec.is_pos === 1;
        return (
          <Tag
            color={inPos ? "green" : "default"}
            className="text-xs font-semibold"
          >
            {inPos ? "Yes" : "No"}
          </Tag>
        );
      },
    },
    {
      title: "Neg Qty",
      key: "is_negative_qty_allowed",
      width: 120,
      render: (_: any, rec: Product) => {
        const allowed = rec.is_negative_qty_allowed === 1;
        return (
          <Tag
            color={allowed ? "warning" : "default"}
            className="text-xs font-semibold"
          >
            {allowed ? "Yes" : "No"}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: string) => (
        <Tag color={s === "Active" ? "success" : "default"}>{s}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 70,
      render: (_: any, rec: Product) => {
        const menuItems = [
          ...(isAllowed(ProductAction.Edit)
            ? [
                {
                  key: "edit",
                  label: "Edit Product",
                  icon: <EditOutlined />,
                  onClick: () => openDrawer(rec),
                },
              ]
            : []),
          {
            key: "Add to POS",
            label: rec.is_pos === 1 ? "Remove from POS" : "Add to POS",
            icon: rec.is_pos === 1 ? <MinusCircleOutlined /> : <PlusOutlined />,
            onClick: () => {
              const newVal: 0 | 1 = rec.is_pos === 1 ? 0 : 1;
              handleTogglePos(rec.id, newVal);
            },
          },
          {
            key: "toggle-negative-qty",
            label:
              rec.is_negative_qty_allowed === 1
                ? "Block Negative Qty"
                : "Allow Negative Qty",
            icon: <MinusCircleOutlined />,
            onClick: () => {
              const newVal: 0 | 1 = rec.is_negative_qty_allowed === 1 ? 0 : 1;
              handleToggleNegativeQty(rec.id, newVal);
            },
          },
          {
            key: "print_barcode",
            label: "Print Barcode Labels",
            icon: <PrinterOutlined />,
            onClick: () => {
              setBarcodePrintProduct(rec);
              setBarcodePrintModalOpen(true);
            },
          },
          {
            key: "history",
            label: "Stock History",
            icon: <HistoryOutlined />,
            onClick: () => {
              setHistoryProduct(rec);
              setHistoryDrawerOpen(true);
            },
          },
          {
            key: "toggle-status",
            label: rec.status === "Active" ? "Deactivate" : "Activate",
            icon: <PoweroffOutlined />,
            danger: rec.status === "Active",
            onClick: () => {
              Modal.confirm({
                title: `Mark as ${rec.status === "Active" ? "Inactive" : "Active"}?`,
                content: `Are you sure you want to ${rec.status === "Active" ? "deactivate" : "activate"} this product?`,
                okText: "Yes",
                cancelText: "No",
                onOk: () => handleToggleStatus(rec),
              });
            },
          },
          ...(isAllowed(ProductAction.Delete)
            ? [
                {
                  key: "delete",
                  label: "Delete Product",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: "Are you sure you want to delete this product?",
                      content: "This action will soft-delete the product.",
                      okText: "Delete",
                      okButtonProps: { danger: true },
                      cancelText: "Cancel",
                      onOk: () => handleDelete(rec.id),
                    });
                  },
                },
              ]
            : []),
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              className="text-gray-500 hover:text-gray-700"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileImport}
        style={{ display: "none" }}
        accept=".xlsx, .xls"
      />

      <PageHeader
        title="Product Management"
        subText={`Total unique items: ${total}`}
        handleButtons={[
          ...(isAllowed(ProductAction.Create)
            ? [
                {
                  title: "Add Product",
                  icon: PlusOutlined,
                  onClick: () => openDrawer(),
                  onMouseEnter: prefetchProductLookups,
                },
              ]
            : []),
          {
            title: "",
            icon: ReloadOutlined,
            onClick: refetch,
          },
        ]}
        searchBar={
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Select
              allowClear
              placeholder="Category Filter"
              onChange={handleCategoryFilter}
              className="w-full sm:w-36"
              onMouseEnter={() => categoriesRequest.mutate()}
            >
              {categories?.map((category: any) => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>

            <Select
              allowClear
              placeholder="Brand Filter"
              onChange={handleBrandFilter}
              className="w-full sm:w-36"
              onMouseEnter={() => brandsRequest.mutate()}
            >
              {brands?.map((brand: any) => (
                <Option key={brand.id} value={brand.id}>
                  {brand.name}
                </Option>
              ))}
            </Select>

            <Input
              ref={searchInputRef}
              placeholder={`Search by Title, SKU, Barcode... [${SHORTCUTS.PRODUCTS.FOCUS_SEARCH.key}]`}
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={handleSearchChange}
              allowClear
              onClear={() => {
                setSearchInput("");
                setPage(1);
              }}
              className="w-full sm:w-56"
            />
          </div>
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <Table
          dataSource={products}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            itemName: "products",
          })}
          scroll={{ x: 1000 }}
          expandable={{
            expandedRowRender: (record) => <StockTable record={record} />,
            rowExpandable: (record) =>
              record.stocks && record.stocks.length > 0,
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No products found"
              />
            ),
          }}
        />
      </div>

      <ProductForm
        open={drawerOpen}
        onClose={closeDrawer}
        loading={submitLoading}
        onSubmit={handleSubmitProduct}
        editingProduct={editingProduct}
        form={form}
        excelSheetUploading={excelSheetUploading}
        triggerImportClick={triggerImportClick}
      />

      <BulkImportReport
        open={importReportOpen}
        onCancel={() => setImportReportOpen(false)}
        successMessage={importSuccessMessage}
        errors={importErrors}
      />

      <StockHistoryDrawer
        open={historyDrawerOpen}
        onClose={closeHistoryDrawer}
        product={historyProduct}
      />

      <ProductBarcodePrintModal
        open={barcodePrintModalOpen}
        onClose={() => {
          setBarcodePrintModalOpen(false);
          setBarcodePrintProduct(null);
        }}
        product={barcodePrintProduct}
      />
    </div>
  );
};
